import { ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import { useFormContext, useWatch } from "react-hook-form";
import type { Category } from "../../../redux/types/categoryTypes";
import {
  genderLabels,
  LocalPhoto,
  returnPolicies,
  selectListingCategory,
  SellingValues,
} from "./types";
import { formatUsd, parsePriceCents } from "./pricing";
import { StepHeading } from "./FormFields";

function ReviewSection({
  title,
  edit,
  children,
}: {
  title: string;
  edit: () => void;
  children: ReactNode;
}) {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ mb: 1 }}
      >
        <Typography component="h3" fontWeight={700} color="secondary.main">
          {title}
        </Typography>
        <Button
          type="button"
          size="small"
          startIcon={<EditOutlined />}
          onClick={edit}
          aria-label={`Edit ${title.toLowerCase()}`}
        >
          Edit
        </Button>
      </Stack>
      {children}
    </Box>
  );
}

export default function ReviewStep({
  categories,
  photos,
  edit,
}: {
  categories: Category[];
  photos: LocalPhoto[];
  edit: (step: number) => void;
}) {
  const { control } = useFormContext<SellingValues>();
  const values = useWatch({ control }) as SellingValues;
  const selection = selectListingCategory(categories, values);
  const price = parsePriceCents(values.price);
  const shipping = parsePriceCents(values.shipping, true);
  return (
    <>
      <StepHeading title="Give Your Listing One Last Look">
        Here's how your toy is shaping up. Review the details and edit anything
        you'd like.
      </StepHeading>
      <Stack spacing={3} sx={{ overflowWrap: "anywhere" }}>
        <ReviewSection title="Photos & condition" edit={() => edit(1)}>
          {photos[0] ? (
            <Box
              component="img"
              src={photos[0].previewUrl}
              alt={values.title.trim()}
              sx={{
                width: "100%",
                height: { xs: 230, sm: 320 },
                objectFit: "contain",
                bgcolor: "#f5f7f9",
                borderRadius: 3,
              }}
            />
          ) : (
            <Box
              sx={{
                p: 4,
                bgcolor: "#f5f7f9",
                textAlign: "center",
                borderRadius: 3,
              }}
            >
              <Inventory2Outlined
                sx={{ fontSize: 40, color: "text.secondary" }}
              />
              <Typography>Reselect your toy photos before listing.</Typography>
            </Box>
          )}
          {photos.length > 1 && (
            <Stack direction="row" gap={1} sx={{ mt: 1 }}>
              {photos.map((photo, index) => (
                <Box
                  key={photo.id}
                  component="img"
                  src={photo.previewUrl}
                  alt={`Toy view ${index + 1}`}
                  sx={{
                    width: 72,
                    height: 64,
                    objectFit: "contain",
                    bgcolor: "#f5f7f9",
                    border: "1px solid",
                    borderColor: index === 0 ? "#b45127" : "divider",
                    borderRadius: 2,
                  }}
                />
              ))}
            </Stack>
          )}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 2 }}
          >
            <Chip
              label={
                values.condition === "new"
                  ? "New"
                  : values.condition === "used"
                    ? "Used"
                    : "Condition needed"
              }
              size="small"
            />
            <Chip
              label={`Quantity: ${values.quantity}`}
              size="small"
              variant="outlined"
            />
          </Stack>
          {values.conditionNotes.trim() && (
            <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
              {values.conditionNotes.trim()}
            </Typography>
          )}
        </ReviewSection>
        <Divider />
        <ReviewSection title="Toy details" edit={() => edit(0)}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {selection
              ? `${selection.category.name} › ${selection.subcategory.name}`
              : "Choose a valid category and subcategory."}
          </Typography>
          <Typography
            component="h3"
            variant="h5"
            fontWeight={750}
            color="secondary.main"
          >
            {values.title.trim()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {values.gender ? genderLabels[values.gender] : "Gender needed"}
          </Typography>
          <Typography sx={{ mt: 2, whiteSpace: "pre-wrap" }}>
            {values.description.trim() || "No additional description."}
          </Typography>
          {(values.upc || values.gtin) && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {values.upc && `UPC: ${values.upc}`}{" "}
              {values.gtin && `GTIN / Identifier: ${values.gtin}`}
            </Typography>
          )}
        </ReviewSection>
        <Divider />
        <ReviewSection title="Pricing" edit={() => edit(2)}>
          <Typography variant="h4" fontWeight={750} color="secondary.main">
            {price === null ? "Price needed" : formatUsd(price)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Per toy · USD
          </Typography>
          {values.minimumOffer.trim() && (
            <Typography sx={{ mt: 1 }}>
              Lowest offer recorded:{" "}
              {formatUsd(parsePriceCents(values.minimumOffer) || 0)}. Offer
              functionality is not connected yet.
            </Typography>
          )}
        </ReviewSection>
        <Divider />
        <ReviewSection title="Shipping & returns" edit={() => edit(3)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography fontWeight={650}>
                {shipping === 0
                  ? "Free Shipping"
                  : shipping === null
                    ? "Shipping charge needed"
                    : `${formatUsd(shipping)} flat shipping`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                United States only
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Packed box: {values.length} × {values.width} × {values.height}{" "}
                in
                <br />
                Weight: {values.pounds || "0"} lb {values.ounces || "0"} oz
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography fontWeight={650}>
                {values.returns
                  ? returnPolicies[values.returns]?.label
                  : "Return policy needed"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {values.returns
                  ? returnPolicies[values.returns]?.detail
                  : "Choose your return preference."}
              </Typography>
            </Grid>
          </Grid>
        </ReviewSection>
        <Alert severity="info">
          Publishing is unavailable while secure listing submission is being
          connected. Your toy has not been listed. You can save a draft on this
          device.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Planned availability policy: listings stay active for 30 days. A
          reminder will ask you to confirm availability. Unconfirmed listings
          become inactive, remain saved, and can be reactivated. This policy is
          not active yet.
        </Typography>
      </Stack>
    </>
  );
}
